import { useHomePage } from "@/pages/home/api";
import GetDoneSuccessIcon from "@/assets/icons/getdon_success.svg?react";
import { useTranslation } from "react-i18next";
import {
  useGetDailyAwardMutation,
  useRecoverStreakMutation,
} from "./daily-check.api";
import { useGetProfile } from "@/pages/profile/api";
import { useQueryClient } from "@tanstack/react-query";
import { boom } from "@/lib/exposion";
import Clock from "@/assets/icons/clock.svg?react";
import { formatNumber } from "@/shared/utils";
import {
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorHandler } from "@/components/error-hanlder/ErrorHandler";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import CheckInDialog from "../../../CheckInDialog";
import StreakLostModal, { PENDING_RECOVERY_KEY } from "./StreakLostModal";
import StreakSavedModal from "./StreakSavedModal";
import { AUTO_OPEN_CHECKIN_KEY } from "@/shared/utils";

const Skeleton = ({ className }: { className?: string }) => (
  <div className={cn("skeleton rounded-md h-2 bg-white/08", className)} />
);

const Loading = () => <Skeleton className="h-[58px] w-full rounded-xl" />;

interface CountdownTimerProps {
  onDayChange?: () => void;
  className?: string;
  targetTime?: string | null;
}

interface TimeLeft {
  hours: string;
  minutes: string;
  seconds: string;
}

export const NextDayCountdown = (props: CountdownTimerProps) => {
  const { onDayChange, className, targetTime } = props;
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    hours: "00",
    minutes: "00",
    seconds: "00",
  });
  const [currentDay, setCurrentDay] = useState(dayjs().date());
  const hasReachedTargetRef = useRef(false);

  const calculateTimeLeft = useCallback(() => {
    const now = dayjs();
    const serverTarget = targetTime ? dayjs(targetTime) : null;
    const nextCheckInAt =
      serverTarget?.isValid()
        ? serverTarget
        : now.add(1, "day").startOf("day");
    const diff = Math.max(nextCheckInAt.diff(now, "second"), 0);

    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    return {
      hours: hours.toString().padStart(2, "0"),
      minutes: minutes.toString().padStart(2, "0"),
      seconds: seconds.toString().padStart(2, "0"),
    };
  }, [targetTime]);

  useEffect(() => {
    hasReachedTargetRef.current = false;
  }, [targetTime]);

  const updateTimer = useCallback(() => {
    const newTime = calculateTimeLeft();
    setTimeLeft(newTime);

    if (targetTime) {
      const target = dayjs(targetTime);
      if (
        target.isValid() &&
        Date.now() >= target.valueOf() &&
        !hasReachedTargetRef.current
      ) {
        hasReachedTargetRef.current = true;
        onDayChange?.();
      }
      return;
    }

    // Fallback for older API responses that do not provide the server target.
    const newDay = dayjs().date();
    if (newDay !== currentDay) {
      setCurrentDay(newDay);
      onDayChange?.();
    }
  }, [calculateTimeLeft, currentDay, onDayChange, targetTime]);

  useEffect(() => {
    updateTimer();
    const timerId = setInterval(updateTimer, 1000);
    return () => clearInterval(timerId);
  }, [updateTimer]);

  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      <p className="text-white text-xs max-[390px]:text-[10px] font-semibold leading-120">
        <span className="time-segment">{timeLeft.hours}</span>
        <span className="time-separator">:</span>
        <span className="time-segment">{timeLeft.minutes}</span>
        <span className="time-separator">:</span>
        <span className="time-segment">{timeLeft.seconds}</span>
      </p>
      <Clock className="size-4 text-white" />
    </div>
  );
};

const DailyCheckIn = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler}>
      <Suspense fallback={<Loading />}>
        <BaseDailyCheckIn />
      </Suspense>
    </ErrorBoundary>
  );
};

interface StreakLostState {
  previous_day_number: number;
  recovery_available_until: string;
  recovery_requires_check_in?: boolean;
}

interface PremiumGraceState {
  missedDate: string;
  previousDayNumber: number;
  currentDayNumber: number;
}

const PREMIUM_GRACE_DISMISS_KEY_PREFIX = "premium-streak-grace-dismissed";

const BaseDailyCheckIn = () => {
  const { t } = useTranslation();
  const { data, isLoading: isHomeLoading } = useHomePage();
  const { data: profile, isLoading: isProfileLoading } = useGetProfile();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useGetDailyAwardMutation();
  const { mutateAsync: recoverStreak } = useRecoverStreakMutation();
  const [isClaimedState, setIsClaimedState] = useState(false);
  const [streakLost, setStreakLost] = useState<StreakLostState | null>(null);
  const [isRecoveryDismissed, setIsRecoveryDismissed] = useState(false);
  const [streakSaved, setStreakSaved] = useState<PremiumGraceState | null>(null);
  const [isCheckInDialogOpen, setIsCheckInDialogOpen] = useState(false);
  const recoveryAttemptKeyRef = useRef<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const isClaimed = useMemo(() => {
    const lastCheck = data?.last_daily_check;
    if (!lastCheck) return false;
    if (typeof lastCheck.is_claimed === "boolean") {
      return lastCheck.is_claimed;
    }
    // Backward-compatible fallback during a rolling API deployment.
    return dayjs().isSame(dayjs(lastCheck.date), "day");
  }, [data]);

  // A Premium user with their last check-in two days ago may still claim
  // today without losing their streak. This must be shown before claiming,
  // otherwise the warning arrives too late to guide the user to the CTA.
  const premiumGraceState = useMemo<PremiumGraceState | null>(() => {
    const lastCheck = data?.last_daily_check;
    if (
      !lastCheck?.premium_grace_available ||
      !lastCheck.premium_grace_missed_date ||
      isClaimed
    ) {
      return null;
    }

    return {
      missedDate: lastCheck.premium_grace_missed_date,
      previousDayNumber: Math.max(lastCheck.day_number - 1, 1),
      currentDayNumber: lastCheck.day_number,
    };
  }, [data, isClaimed]);

  const premiumGraceDismissKey = useMemo(() => {
    if (!premiumGraceState) return null;
    return `${PREMIUM_GRACE_DISMISS_KEY_PREFIX}:${profile?.id ?? "unknown"}:${premiumGraceState.missedDate}`;
  }, [premiumGraceState, profile?.id]);

  const dismissPremiumGrace = useCallback(() => {
    if (premiumGraceDismissKey) {
      try {
        localStorage.setItem(premiumGraceDismissKey, "1");
      } catch {
        // The sheet can still be closed even when browser storage is full.
      }
    }
    setStreakSaved(null);
  }, [premiumGraceDismissKey]);

  const claimPremiumGrace = useCallback(() => {
    dismissPremiumGrace();
    setIsCheckInDialogOpen(true);
  }, [dismissPremiumGrace]);

  // Open the warning once per skipped date and user. Closing it with X must
  // not reopen it until a future premium-grace event occurs.
  useEffect(() => {
    if (
      isHomeLoading ||
      isProfileLoading ||
      !premiumGraceState ||
      !premiumGraceDismissKey ||
      streakSaved
    ) {
      return;
    }

    try {
      if (localStorage.getItem(premiumGraceDismissKey) === "1") return;
    } catch {
      // Continue without deduplication when browser storage is unavailable.
    }

    setStreakSaved(premiumGraceState);
  }, [
    isHomeLoading,
    isProfileLoading,
    premiumGraceDismissKey,
    premiumGraceState,
    streakSaved,
  ]);

  // Backend marks today's claim as `was_reset` while the 24h recovery
  // window is open. We surface a pulsing alert badge on the card while
  // that holds — once the deadline passes the field flips back to false
  // (server-side) and the badge vanishes naturally on the next refetch.
  const recoveryState = useMemo<StreakLostState | null>(() => {
    const c = data?.last_daily_check;
    if (!c?.was_reset || !c.previous_day_number || !c.recovery_available_until) {
      return null;
    }
    if (new Date(c.recovery_available_until).getTime() <= Date.now()) {
      return null;
    }
    return {
      previous_day_number: c.previous_day_number,
      recovery_available_until: c.recovery_available_until,
      recovery_requires_check_in: c.recovery_requires_check_in,
    };
  }, [data]);

  useEffect(() => {
    if (!recoveryState || streakLost || isRecoveryDismissed) return;
    try {
      localStorage.setItem(PENDING_RECOVERY_KEY, JSON.stringify(recoveryState));
    } catch {
      /* storage full — modal still works from home state */
    }
    setStreakLost(recoveryState);
  }, [recoveryState, streakLost, isRecoveryDismissed]);

  const getDailyAward = async (): Promise<boolean> => {
    if (isClaimed) return false;

    let res;
    try {
      res = await mutateAsync();
    } catch {
      showCustomToast(t("global_error"), "error");
      queryClient.invalidateQueries({ queryKey: ["navigation"] });
      return false;
    }

    // Reset detected: do not show the normal day-1 success flow first.
    // The recovery bottom sheet is the primary state until the 24h window
    // expires or Premium restores the streak.
    if (res.was_reset && res.previous_day_number && res.recovery_available_until) {
      const nextRecoveryState = {
        previous_day_number: res.previous_day_number,
        recovery_available_until: res.recovery_available_until,
        recovery_requires_check_in: res.recovery_requires_check_in,
      };
      try {
        localStorage.setItem(
          PENDING_RECOVERY_KEY,
          JSON.stringify(nextRecoveryState)
        );
      } catch {
        /* storage full — modal still works via DB state */
      }
      setIsRecoveryDismissed(false);
      setStreakLost(nextRecoveryState);
      return true;
    }

    setIsClaimedState(true);
    showCustomToast(t("congratulations"), "success", {
      description: t("get_done_successfully", {
        don: formatNumber(res.total_awarded_don),
      }),
      icon: <GetDoneSuccessIcon className="size-7" />,
    });
    boom();
    return true;
  };

  const handleCheckInDayChange = useCallback(() => {
    setIsClaimedState(false);
    queryClient.invalidateQueries({ queryKey: ["navigation"] });
  }, [queryClient]);

  // While a reset can still be recovered, keep profile/home fresh. Payment
  // callbacks complete outside the mini-app, so relying only on the cached
  // profile can miss the premium activation and leave the streak unrecovered.
  useEffect(() => {
    const pending = recoveryState ?? streakLost;
    if (!pending?.recovery_available_until || profile?.is_premium) return;

    const deadline = new Date(pending.recovery_available_until).getTime();
    if (deadline <= Date.now()) return;

    const refresh = () => {
      if (Date.now() >= deadline) return;
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["navigation"] });
    };

    refresh();
    const intervalId = window.setInterval(refresh, 5000);
    return () => window.clearInterval(intervalId);
  }, [profile?.is_premium, queryClient, recoveryState, streakLost]);

  // Watch for premium activation: if a recovery is pending and the user is
  // premium now, call /RecoverStreak automatically. This also handles reloads
  // where the first profile response is already `is_premium=true`.
  useEffect(() => {
    if (isProfileLoading || !profile) return;
    if (!profile.is_premium) return;

    let pending: StreakLostState | null = null;
    try {
      const raw = localStorage.getItem(PENDING_RECOVERY_KEY);
      if (raw) pending = JSON.parse(raw) as StreakLostState;
    } catch {
      pending = null;
    }
    pending = pending ?? recoveryState ?? streakLost;
    if (!pending?.recovery_available_until) return;

    if (pending.recovery_requires_check_in) {
      // No DailyCheck row exists before the user claims. Once Premium is
      // active, refetch Homepage so its Premium-grace CTA guides that claim
      // instead of calling RecoverStreak and silently receiving a 404/409.
      localStorage.removeItem(PENDING_RECOVERY_KEY);
      setStreakLost(null);
      queryClient.invalidateQueries({ queryKey: ["navigation"] });
      return;
    }

    const attemptKey = `${pending.previous_day_number}:${pending.recovery_available_until}`;
    if (recoveryAttemptKeyRef.current === attemptKey) return;

    if (new Date(pending.recovery_available_until).getTime() < Date.now()) {
      // Window expired — nothing to do, drop the flag.
      localStorage.removeItem(PENDING_RECOVERY_KEY);
      return;
    }

    recoveryAttemptKeyRef.current = attemptKey;
    (async () => {
      try {
        const res = await recoverStreak();
        localStorage.removeItem(PENDING_RECOVERY_KEY);
        setStreakLost(null);
        showCustomToast(
          t("streak_recovered_toast", { day: res.day_number }) ||
            `Streak tiklandi! Endi day ${res.day_number} dan davom etasiz`,
          "success",
          {
            icon: <GetDoneSuccessIcon className="size-7" />,
          }
        );
        boom();
      } catch {
        // Backend reject (premium expired again, deadline passed, race) —
        // silently clear the flag; modal countdown will hide CTA on expiry.
        localStorage.removeItem(PENDING_RECOVERY_KEY);
      }
    })();
  }, [
    profile,
    isProfileLoading,
    queryClient,
    recoverStreak,
    t,
    recoveryState,
    streakLost,
  ]);

  const handleCardClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    // Clicks on the inner button already open the dialog naturally.
    if ((e.target as HTMLElement).closest("button")) return;
    if (recoveryState) {
      setIsRecoveryDismissed(false);
      setStreakLost(recoveryState);
      return;
    }
    cardRef.current?.querySelector("button")?.click();
  };

  // Deep link (startapp=checkin, from the daily check-in reminder bot
  // message): App.tsx drops AUTO_OPEN_CHECKIN_KEY and routes home. Once
  // this card is mounted with data, consume the flag and open the dialog
  // the same way a manual click does.
  useEffect(() => {
    if (isHomeLoading || isProfileLoading) return;
    if (sessionStorage.getItem(AUTO_OPEN_CHECKIN_KEY) !== "1") return;
    sessionStorage.removeItem(AUTO_OPEN_CHECKIN_KEY);
    const timeoutId = setTimeout(() => {
      if (recoveryState) {
        setIsRecoveryDismissed(false);
        setStreakLost(recoveryState);
        return;
      }
      cardRef.current?.querySelector("button")?.click();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [isHomeLoading, isProfileLoading, recoveryState]);

  if (isHomeLoading || isProfileLoading) {
    return <Loading />;
  }

  return (
    <Fragment>
      <div
        ref={cardRef}
        onClick={handleCardClick}
        className="relative py-2.5 pr-3 pl-[92px] bg-white/08 rounded-xl border-b border-white/08 flex-y-center gap-2 cursor-pointer"
      >
        <img
          className="absolute w-[77px] h-[57px] left-0 gap-0.5  bottom-0 shrink-0"
          src="images/patterns/check-in.webp"
          alt=""
        />
        <div className="flex flex-col gap-1 w-full">
          <p className="text-base font-semibold  leading-130 text-white">
            {t("daily_reward")}
            {/* {!isClaimed
              ? t("daily_reward")
              : data && (
                  <Fragment>
                    {t("nth_day", {
                      day: data.last_daily_check?.day_number ?? 1,
                    })}
                  </Fragment>
                )} */}
          </p>
          <p className="text-small leading-130 text-gray-1">
            {isClaimed ? t("wait_until_tomorrow") : t("get_todays_don")}
          </p>
        </div>
        {data && (
          <CheckInDialog
            onConfirm={getDailyAward}
            isPending={isPending}
            currentDay={data.last_daily_check?.day_number ?? 1}
            isClaimed={isClaimed || isClaimedState}
            open={isCheckInDialogOpen}
            onOpenChange={setIsCheckInDialogOpen}
            nextCheckInAt={data.last_daily_check?.next_check_in_at}
            onDayChange={handleCheckInDayChange}
            weeklyAwards={data.last_daily_check?.weekly_awards ?? []}
            showRecoveryAlert={!!recoveryState}
            onRecoveryAlertClick={
              recoveryState
                ? () => {
                    setIsRecoveryDismissed(false);
                    setStreakLost(recoveryState);
                  }
                : undefined
            }
            previousDayNumber={recoveryState?.previous_day_number}
          />
        )}
        {/* {data && isInSevendDays ? (
          <div></div>
        ) : (
          <TaskBaseButton
            className={cn(
              "bg-yellow-linear min-w-[4rem] text-black shrink-0 !w-auto ",
              {
                "bg-white/08 text-white pointer-events-none":
                  isClaimed || isClaimedState || isPending,
              }
            )}
            onClick={getDailyAward}
          >
            {isClaimed || isClaimedState ? (
              <NextDayCountdown onDayChange={forceUpdate} />
            ) : (
              <>
                {isPending ? (
                  <Spinner />
                ) : (
                  <>
                    {t("get_don")}
                    <ChevronRight className="text-black size-5" />
                  </>
                )}
              </>
            )}
          </TaskBaseButton>
        )} */}
      </div>
      {streakLost && (
        <StreakLostModal
          isOpen={!!streakLost}
          previousDayNumber={streakLost.previous_day_number}
          recoveryAvailableUntil={streakLost.recovery_available_until}
          recoveryRequiresCheckIn={streakLost.recovery_requires_check_in}
          onClose={() => {
            setIsRecoveryDismissed(true);
            setStreakLost(null);
          }}
        />
      )}
      {streakSaved && (
        <StreakSavedModal
          isOpen={!!streakSaved}
          previousDayNumber={streakSaved.previousDayNumber}
          currentDayNumber={streakSaved.currentDayNumber}
          celebrate={false}
          onClose={dismissPremiumGrace}
          onClaim={claimPremiumGrace}
        />
      )}
    </Fragment>
  );
};

export default DailyCheckIn;
